import { useCallback, useEffect, useRef, useState } from "react";
import type { components } from "@dolfin/api-public";
import { useDolfinClient } from "@/components/DolfinProvider";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

type Template = components["schemas"]["InvoicePdfTemplate"];

// The request carries no invoice data, so the backend fills every field with placeholders and saves nothing.
export function InvoicePdfPreview({ template }: { template: Template }) {
  const api = useDolfinClient();
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .POST("/v1/invoices/pdf-preview", {
        body: { pdfTemplate: template },
        parseAs: "blob",
      })
      .then(({ data, response }) => {
        if (cancelled) return;
        if (!response.ok || !(data instanceof Blob)) {
          setError(
            response.status === 403
              ? "You do not have access to invoice previews for this organisation."
              : "The preview could not be loaded.",
          );
          return;
        }
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = URL.createObjectURL(data);
        setUrl(urlRef.current);
      })
      .catch(() => {
        if (!cancelled) setError("The preview could not be loaded.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, template, attempt]);

  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Preview with placeholder data</span>
        {loading && <Spinner />}
      </div>
      {error ? (
        <div className="flex items-center gap-3">
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
          <Button type="button" variant="outline" size="sm" onClick={retry}>
            Retry
          </Button>
        </div>
      ) : (
        url && (
          <iframe
            title={`${template} invoice PDF preview`}
            src={url}
            className="h-[560px] w-full rounded-md border bg-white"
          />
        )
      )}
    </div>
  );
}
